import type {
  ConfigurationStatement,
  ConfigurationStatementInput,
  StatementCompositeTarget,
} from '@repo/frontend-types';
import { entityIdentityKey as key, renumberBid } from '@repo/front-end-utils';

/**
 * Which written totals Go Live would remove or weaken — computed BEFORE anything destructive runs,
 * so the operator is asked (`refiling-preserves-configuration`, epic ledger row 121).
 *
 * The observer loss (`computeObserverLoss`) says which entities stop being measured. It could not
 * see this: on staging PUL-CON, 2026-09-24, Go Live lost both floor totals and filed the whole-gym
 * total with no members, and the dialog said nothing, because the loss was of TOTALS, not of an
 * entity's last observer.
 *
 * Pure: two statements in, a list out. It compares the written totals running now (`composites`)
 * with the ones about to be filed, target by target.
 *
 * ⛔ **A total's members are its inputs: areas, floors, machines and other totals. Never cameras**
 * (`launch-single-confirmation`, Rob 2026-10-28). Fewer cameras on something that is still measured
 * is not a loss, so:
 *
 *  - **Automatic totals** — the ones the filing derives for a target watched by two or more cameras
 *    — are never reported, whatever happens to their cameras.
 *  - A written total's **own cameras** are not counted as members, and a written total whose only
 *    members are its own cameras is judged like an automatic one: never reported.
 *  - A written total **gaining** a member nothing can satisfy is not a loss against what runs today;
 *    the Launch page's composite verdicts already show a total that will go live short.
 *
 * ⚠ **It predicts; the server decides.** A member the filing cannot satisfy is recorded, not refused
 * (the empty-total draft rule, Rob 2026-09-24). This names the members that will no longer be
 * satisfied before the write, using the server's satisfaction rule: a member is satisfied by an
 * observer filed on it, or by a total filed for it.
 */

export interface CompositeLossEntry {
  /** The target's identifier (`PUL-CON-Ground Floor`), or the twin's for a whole-twin total. */
  target: string;
  /**
   * How many members (inputs) it has now, and how many of them would still be satisfied. For a total
   * the new version does not file at all, `to` is 0.
   */
  from: number;
  to: number;
  /**
   * Written by a person, or maintained automatically from the target's own cameras. Always
   * `'authored'` since `launch-single-confirmation`: automatic totals are never a loss. Kept so the
   * result's shape, and the re-check's fingerprint of it, are unchanged.
   */
  kind: 'authored' | 'automatic';
}

export interface CompositeLoss {
  /** Totals the new version would not hold at all. */
  removed: CompositeLossEntry[];
  /** Totals it would hold with fewer members than they have now. */
  losingMembers: CompositeLossEntry[];
}

/** Nothing lost. Frozen, so a caller cannot append to the one shared instance. */
export const NO_COMPOSITE_LOSS: CompositeLoss = Object.freeze({
  removed: [],
  losingMembers: [],
}) as CompositeLoss;

/** A target's identity, as the filing keys it. `null` for a target nothing can name. */
const targetKey = (target: StatementCompositeTarget, twinBid: string): string | null => {
  if (target.kind === 'twin') return key(twinBid);
  if (target.kind === 'floor' || target.kind === 'entity') return key(target.bid);
  return null;
};

const targetLabel = (target: StatementCompositeTarget, twinBid: string): string =>
  target.kind === 'twin' ? twinBid : target.kind === 'unknown' ? target.name : target.bid;

/**
 * Cameras per target, counting only observers that actually measure — a stream AND a region. The
 * same rule as `selectFileableObservers`: an observer with one and not the other measures nothing,
 * so it cannot satisfy a member.
 */
const camerasPerTarget = (
  observers: Array<{ entityBid: string; rtspEndpointId?: number | null; polygon?: unknown }>,
): Map<string, number> => {
  const counts = new Map<string, number>();
  for (const observer of observers) {
    if (observer.rtspEndpointId == null || observer.polygon == null) continue;
    const k = key(observer.entityBid);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
};

/**
 * `running` with every renumbered space read under its new BID (`floor-plan-page-launch-bid-changes`,
 * R13): observers' `entityBid`, and entity-kind composite targets and inputs. The filing carries those
 * identifiers to the new BID, so comparing the running totals under their old BIDs would report a
 * renumbered space's total as removed. An empty map returns `running` itself.
 */
export const renumberStatement = (
  running: ConfigurationStatement,
  renames: ReadonlyMap<string, string>,
): ConfigurationStatement => {
  if (renames.size === 0) return running;
  const target = (t: StatementCompositeTarget): StatementCompositeTarget =>
    t.kind === 'entity' ? { ...t, bid: renumberBid(t.bid, renames) } : t;
  return {
    ...running,
    observers: running.observers.map(o => ({ ...o, entityBid: renumberBid(o.entityBid, renames) })),
    composites: running.composites.map(c => ({
      ...c,
      target: target(c.target),
      inputs: c.inputs.map(target),
    })),
  };
};

export const computeCompositeLoss = ({
  running,
  filing,
  twinBid,
}: {
  running: ConfigurationStatement;
  filing: ConfigurationStatementInput;
  twinBid: string;
}): CompositeLoss => {
  const removed: CompositeLossEntry[] = [];
  const losingMembers: CompositeLossEntry[] = [];

  const filedCameras = camerasPerTarget(filing.observers);
  const filedAuthored = new Map<string, (typeof filing.composites)[number]>();
  for (const composite of filing.composites) {
    const k = targetKey(composite.target, twinBid);
    if (k) filedAuthored.set(k, composite);
  }

  const runningCameras = camerasPerTarget(running.observers);
  const runningAuthored = new Set<string>();
  for (const composite of running.composites) {
    const k = targetKey(composite.target, twinBid);
    if (k) runningAuthored.add(k);
  }

  /**
   * Is this member satisfied, by a measuring camera on it or a total for it? The server's rule,
   * applied to the version running now and, predicted, to the one about to be filed.
   */
  const satisfiedBy =
    (cameras: Map<string, number>, totals: { has: (k: string) => boolean }) =>
    (input: StatementCompositeTarget): boolean => {
      const k = targetKey(input, twinBid);
      if (k === null) return false;
      return (cameras.get(k) ?? 0) > 0 || totals.has(k);
    };
  const satisfiedNow = satisfiedBy(runningCameras, runningAuthored);
  const satisfied = satisfiedBy(filedCameras, filedAuthored);

  for (const composite of running.composites) {
    // Only its own cameras: judged like an automatic total, so never a loss.
    if (composite.inputs.length === 0) continue;
    /*
     * Members satisfied TODAY, not merely named (R1c, "measured today"). A total already short of a
     * member nothing measures is not losing it, so a launch that changes nothing stays silent (R5);
     * counting every named input would ask about that total on every launch.
     */
    const from = composite.inputs.filter(satisfiedNow).length;
    // Not measuring anything today, so there is nothing for this launch to take away.
    if (from === 0) continue;
    const k = targetKey(composite.target, twinBid);
    const label = targetLabel(composite.target, twinBid);
    // A target nothing can name cannot be filed back at all — say so rather than skip it.
    const filed = k === null ? undefined : filedAuthored.get(k);
    if (!filed) {
      removed.push({ target: label, from, to: 0, kind: 'authored' });
      continue;
    }
    // Members still satisfied. A member added that nothing can satisfy is not a loss (R2f), and the
    // total's own cameras are not members, so neither appears here.
    const to = filed.inputs.filter(satisfied).length;
    if (to < from) losingMembers.push({ target: label, from, to, kind: 'authored' });
  }

  const byTarget = (a: CompositeLossEntry, b: CompositeLossEntry) =>
    a.target.localeCompare(b.target);
  removed.sort(byTarget);
  losingMembers.sort(byTarget);
  return { removed, losingMembers };
};
